// ProjectNew.js
// Step 2 of onboarding: create a client project, auto-trigger ENQ discovery,
// show a waiting screen, then redirect to the Discovery Report.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';

const TEAL  = '#2a8ba8';
const DARK  = '#39393a';
const LIGHT = '#f5f9fa';
const ERROR = '#c0392b';
const INDUSTRIES = [
  { value: 'financial_services',     label: 'Financial Services' },
  { value: 'education',              label: 'Education' },
  { value: 'health_pharma',          label: 'Health & Pharma' },
  { value: 'technology',             label: 'Technology & SaaS' },
  { value: 'ecommerce',              label: 'E-commerce & Retail' },
  { value: 'professional_services',  label: 'Professional Services' },
  { value: 'travel_hospitality',     label: 'Travel & Hospitality' },
  { value: 'media_publishing',       label: 'Media & Publishing' },
  { value: 'charity_nonprofit',      label: 'Charity & Non-profit' },
  { value: 'property_real_estate',   label: 'Property & Real Estate' },
  { value: 'marketing_agency',       label: 'Marketing Agency' },
  { value: 'other',                  label: 'Other' },
];
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: LIGHT,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '520px',
  },
  logo: {
    height: '48px',
    marginBottom: '32px',
    display: 'block',
  },
  breadcrumb: {
    fontSize: '13px',
    color: '#6b7280',
    marginBottom: '24px',
    fontWeight: '500',
  },
  breadcrumbLink: {
    color: TEAL,
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'color 0.2s',
  },
  breadcrumbSeparator: {
    margin: '0 8px',
    color: '#d1d5db',
  },
  heading: {
    fontSize: '24px',
    fontWeight: '700',
    color: DARK,
    margin: '0 0 8px',
  },
  subheading: {
    fontSize: '14px',
    color: '#666',
    margin: '0 0 32px',
    lineHeight: '1.5',
  },
  fieldGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: DARK,
    marginBottom: '6px',
  },
  labelOptional: {
    fontWeight: '400',
    color: '#999',
    fontSize: '12px',
    marginLeft: '4px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: '1.5px solid #ddd',
    borderRadius: '8px',
    outline: 'none',
    fontFamily: 'inherit',
    color: DARK,
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: '1.5px solid #ddd',
    borderRadius: '8px',
    outline: 'none',
    fontFamily: 'inherit',
    color: DARK,
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    cursor: 'pointer',
    appearance: 'auto',
  },
  hint: {
    fontSize: '12px',
    color: '#999',
    marginTop: '6px',
    lineHeight: '1.5',
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: TEAL,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    fontFamily: 'inherit',
    transition: 'background-color 0.2s',
  },
  buttonDisabled: {
    backgroundColor: '#aaa',
    cursor: 'not-allowed',
  },
  error: {
    backgroundColor: '#fdf0ef',
    border: `1px solid ${ERROR}`,
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '13px',
    color: ERROR,
    marginTop: '16px',
  },
  // Waiting screen
  waitingCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '480px',
    textAlign: 'center',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: `4px solid ${LIGHT}`,
    borderTop: `4px solid ${TEAL}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 24px',
  },
  waitingHeading: {
    fontSize: '20px',
    fontWeight: '700',
    color: DARK,
    margin: '0 0 12px',
  },
  waitingText: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.6',
    margin: '0 0 24px',
  },
  waitingSteps: {
    textAlign: 'left',
    backgroundColor: LIGHT,
    borderRadius: '8px',
    padding: '16px 20px',
    fontSize: '13px',
    color: '#555',
    lineHeight: '2',
  },
};
// Inject keyframe animation
const styleSheet = document.styleSheets[0];
try {
  styleSheet.insertRule(`@keyframes spin { to { transform: rotate(360deg); } }`, 0);
} catch (_) {}
// ── Waiting screen ─────────────────────────────────────────────────────────
function WaitingScreen({ projectName }) {
  return (
    <div style={styles.page}>
      <div style={styles.waitingCard}>
        <div style={styles.spinner} />
        <h2 style={styles.waitingHeading}>Discovering {projectName}'s entity landscape</h2>
        <p style={styles.waitingText}>
          GEO Jetpack is crawling the website, extracting brand entities, and mapping the competitive landscape.
          This takes 3–8 minutes.
        </p>
        <div style={styles.waitingSteps}>
          <div>🔍 Crawling up to 75 pages</div>
          <div>🧠 Extracting entities with ENQ™</div>
          <div>🏷️ Classifying into 15 entity types</div>
          <div>🔄 Running competitor reverse-crawl</div>
          <div>✅ Building your entity surface</div>
        </div>
        <p style={{ ...styles.hint, marginTop: '20px', textAlign: 'center' }}>
          You'll be taken to your Discovery Report automatically when it's ready.
        </p>
      </div>
    </div>
  );
}
// ── Main component ─────────────────────────────────────────────────────────
export default function ProjectNew({ apiBaseUrl }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const userId = user?.id;
console.log('🔴 ProjectNew mounted. userId:', userId, 'user object:', user);
  
  const [phase, setPhase]               = useState('form'); // 'form' | 'waiting'
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  // Form fields
  const [projectName, setProjectName]   = useState('');
  const [domain, setDomain]             = useState('');
  const [industry, setIndustry]         = useState('');
  const [competitors, setCompetitors]   = useState('');
  const [otherNames, setOtherNames]     = useState('');
  const canSubmit = projectName.trim() && domain.trim() && industry;
  const normaliseDomain = (raw) => {
    let d = raw.trim().toLowerCase();
    d = d.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
    return d;
  };
  const parseList = (raw) =>
    raw
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!userId) {
      setError('Authentication required. Please refresh and try again.');
      return;
    }
    setLoading(true);
    setError(null);
    const body = {
      name: projectName.trim(),
      domain: normaliseDomain(domain),
      industry,
    };
    if (otherNames.trim()) {
      body.other_names = parseList(otherNames);
    }
    if (competitors.trim()) {
      body.seeded_competitors = parseList(competitors);
    }
    try {
      // Step 1 — create project
      const res = await fetch(`${apiBaseUrl}/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to create project');
      }
      const projectId = data.project?.id;
      if (!projectId) throw new Error('No project ID returned');
      // Step 2 — show waiting screen immediately
      setPhase('waiting');
      // Step 3 — trigger ENQ worker
      await fetch(`${apiBaseUrl}/admin/enq-worker/tick?async=true`, {
        method: 'POST',
        headers: { 'x-user-id': userId },
      });
      // Step 4 — poll enq_status until discovery_complete or discovery_failed
      const poll = async () => {
        try {
          const statusRes = await fetch(`${apiBaseUrl}/projects/${projectId}/discovery`, {
            headers: { 'x-user-id': userId },
          });
          const statusData = await statusRes.json();
          const status = statusData?.project?.enq_status;
          if (status === 'discovery_complete') {
            navigate(`/project/${projectId}/discovery`);
            return;
          }
          if (status === 'discovery_failed') {
            // Still navigate — user can see what was found and retry
            navigate(`/project/${projectId}/discovery`);
            return;
          }
        } catch (_) {
          // Swallow poll errors, keep trying
        }
        setTimeout(poll, 15000); // poll every 15s
      };
      // Start polling after 30s initial delay (ENQ takes a few minutes)
      setTimeout(poll, 30000);
    } catch (err) {
      setLoading(false);
      setPhase('form');
      setError(err.message);
    }
  };
  if (phase === 'waiting') {
    return <WaitingScreen projectName={projectName} />;
  }
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Breadcrumb */}
        <div style={styles.breadcrumb}>
          <span
            style={styles.breadcrumbLink}
            onClick={() => navigate('/dashboard')}
            onMouseEnter={(e) => e.currentTarget.style.color = '#1f6f8f'}
            onMouseLeave={(e) => e.currentTarget.style.color = TEAL}
          >
            Dashboard
          </span>
          <span style={styles.breadcrumbSeparator}>/</span>
          <span
            style={styles.breadcrumbLink}
            onClick={() => navigate('/projects')}
            onMouseEnter={(e) => e.currentTarget.style.color = '#1f6f8f'}
            onMouseLeave={(e) => e.currentTarget.style.color = TEAL}
          >
            Projects
          </span>
          <span style={styles.breadcrumbSeparator}>/</span>
          <span style={{ color: '#6b7280' }}>New</span>
        </div>
        <h1 style={styles.heading}>Add a new project</h1>
        <p style={styles.subheading}>
          GEO Jetpack will crawl your site and those of your competitors to build your entity bank. Typically takes 5-10 minutes. You'll receive an email when complete.
        </p>
        {/* Client name */}
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="projectName">Client or brand name</label>
          <input
            id="projectName"
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="e.g. Acme Financial"
            style={styles.input}
            onFocus={(e) => (e.target.style.borderColor = TEAL)}
            onBlur={(e) => (e.target.style.borderColor = '#ddd')}
            autoFocus
          />
        </div>
        {/* Domain */}
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="domain">Website domain</label>
          <input
            id="domain"
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="e.g. acmefinancial.co.uk"
            style={styles.input}
            onFocus={(e) => (e.target.style.borderColor = TEAL)}
            onBlur={(e) => (e.target.style.borderColor = '#ddd')}
          />
          <p style={styles.hint}>No need to include https:// or www.</p>
        </div>
        {/* Industry */}
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="industry">Industry</label>
          <select
            id="industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            style={styles.select}
            onFocus={(e) => (e.target.style.borderColor = TEAL)}
            onBlur={(e) => (e.target.style.borderColor = '#ddd')}
          >
            <option value="">Select an industry…</option>
            {INDUSTRIES.map((i) => (
              <option key={i.value} value={i.value}>{i.label}</option>
            ))}
          </select>
        </div>
        {/* Competitors — optional */}
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="competitors">
            Main competitors
            <span style={styles.labelOptional}>(optional)</span>
          </label>
          <input
            id="competitors"
            type="text"
            value={competitors}
            onChange={(e) => setCompetitors(e.target.value)}
            placeholder="e.g. Rival Co, Another Brand, Third Option"
            style={styles.input}
            onFocus={(e) => (e.target.style.borderColor = TEAL)}
            onBlur={(e) => (e.target.style.borderColor = '#ddd')}
          />
          <p style={styles.hint}>Comma-separated. Helps ENQ build competitive prompts.</p>
        </div>
        {/* Other names — optional */}
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="otherNames">
            Other brand names or abbreviations
            <span style={styles.labelOptional}>(optional)</span>
          </label>
          <input
            id="otherNames"
            type="text"
            value={otherNames}
            onChange={(e) => setOtherNames(e.target.value)}
            placeholder="e.g. Acme, AF, Acme Finance Ltd"
            style={styles.input}
            onFocus={(e) => (e.target.style.borderColor = TEAL)}
            onBlur={(e) => (e.target.style.borderColor = '#ddd')}
          />
          <p style={styles.hint}>How AI engines might refer to the brand.</p>
        </div>
        {error && <div style={styles.error}>{error}</div>}
        <button
          onClick={handleSubmit}
          disabled={loading || !canSubmit}
          style={{
            ...styles.button,
            ...(loading || !canSubmit ? styles.buttonDisabled : {}),
          }}
        >
          {loading ? 'Creating project…' : 'Start ENQ Discovery →'}
        </button>
      </div>
    </div>
  );
}
